import { animate, inView, stagger } from "motion";

const animateSlideReveal = (el) => {
  animate(el, { x: [0, "100%"] }, { duration: 1.2, delay: 0.2, easing: "ease" });
};

const variants = {
  "fade-up": {
    opacity: 1,
    transform: "translate3d(0, 0, 0)",
  },
  "slide-up": {
    transform: "translate3d(0, 0, 0)",
  },
  "fade-up-left": {
    opacity: 1,
    transform: "translate3d(0, 0, 0)",
  },
  "fade-down": {
    opacity: 1,
    transform: "translate3d(0, 0, 0)",
  },
  "fade-left": {
    opacity: 1,
    transform: "translate3d(0, 0, 0)",
  },
  "fade-left-slow": {
    opacity: 1,
    transform: "translate3d(0, 0, 0)",
  },
  "fade-right": {
    opacity: 1,
    transform: "translate3d(0, 0, 0)",
  },
  "slide-right": {
    transform: "translate3d(0, 0, 0)",
  },
  "fade-in": {
    opacity: 1,
  },
  "scale-in": {
    opacity: 1,
    transform: ["scale(0.6)", "scale(1)"],
  },
  "scale-out": {
    opacity: 1,
    transform: ["scale(2)", "scale(1)"],
  },
  "scale-out-large": {
    opacity: 1,
    transform: ["scale(4)", "scale(1)"],
  },
  draw: {
    pathLength: [0, 1],
  },
};

const reveal = (group) => {
  if (group.dataset.srGroup === "sequence") {
    const sequence = [];
    const sequenceDuration = parseFloat(group.dataset.sequenceDuration) || 0.6;

    group.querySelectorAll(`[data-sr]`).forEach((el) => {
      const variant = variants[el.dataset.sr];

      if (variant && el.style.display !== "none") {
        sequence.push([el, variant, { duration: sequenceDuration, at: "-0.2" }]);
      }
    });

    inView(
      group,
      () => {
        animate(sequence).then(() => {
          group.setAttribute("data-sr-done", true);
        });
      },
      {
        margin: "0px 0px -25% 0px",
      },
    );
  } else {
    for (const [key, opts] of Object.entries(variants)) {
      const els = Array.from(group.querySelectorAll(`[data-sr="${key}"]:not([data-sr-done])`)).filter((el) => {
        return window.getComputedStyle(el).display !== "none" && el.offsetHeight !== 0;
      });

      let duration = 0.8;

      if (key === "fade-in" || key === "fade-left-slow" || key === "scale-out" || key === "scale-out-large" || key === "draw") {
        duration = 1.1;
      }

      if (els.length) {
        els.forEach((el, index) => {
          const checklists = el.querySelectorAll("[class*=as-checklist], ul.as-list");

          if (checklists.length) {
            let children = [];

            Array.from(el.children).forEach((child) => {
              if (
                ["as-list", "as-checklist-red", "as-checklist-lg-red"].some((className) =>
                  child.classList.contains(className),
                )
              ) {
                const lis = Array.from(child.querySelectorAll("li"));

                children = [...children, ...lis];
              } else {
                children.push(child);
              }
            });

            if (!el.querySelector(".ignore-sr")) {
              children.map((child) => child.setAttribute("data-sr", "fade-up"));

              el.removeAttribute("data-sr");
              els.splice(index, 1, ...children);
            }
          }
        });

        let staggerAmount = 0.1;

        if (els.length > 15) {
          staggerAmount = 0.03;
        }

        if (group.dataset.srGroup === "delay") {
          staggerAmount = 0.5;
        }

        const doAnimation = () => {
          if (key === "draw") {
            els.forEach((path) => {
              path.style.strokeDasharray = null;
              path.style.strokeDashoffset = null;
            });
          }

          animate(els, opts, {
            duration,
            delay: stagger(staggerAmount),
          }).then(() => {
            els.forEach((el) => {
              el.setAttribute("data-sr-done", true);
            });

            group.setAttribute("data-sr-done", true);
          });
        };

        if (group.dataset.srGroup === "onload") {
          doAnimation();
        } else {
          inView(group, doAnimation, {
            margin: "0px 0px -25% 0px",
          });
        }
      }
    }
  }
};

const handleMutations = (group, mutationList) => {
  for (const mutation of mutationList) {
    if (mutation.type === "childList") {
      reveal(group);
    }
  }
};

export default {
  init: () => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }

    document.querySelectorAll('[data-sr="draw"]').forEach((path) => {
      path.style.strokeDasharray = path.getTotalLength();
      path.style.strokeDashoffset = path.getTotalLength();
    });

    document.querySelectorAll("[data-slide-reveal]").forEach((el) => {
      const observer = new IntersectionObserver(([e]) => {
        if (e.isIntersecting) {
          animateSlideReveal(el);

          observer.disconnect();
        }
      });

      observer.observe(el);
    });

    document.querySelectorAll("[data-sr-group]").forEach((group) => {
      if (group.closest("[data-disable-animations]")) {
        group.removeAttribute("data-sr-group");
        return;
      }

      if (group.dataset.srGroup === "mobile") {
        if (window.matchMedia("(max-width: 767px)").matches) {
          reveal(group);
        }

        return;
      }

      if (group.dataset.srGroup === "observe") {
        const observer = new MutationObserver(handleMutations.bind(null, group));

        observer.observe(group, {
          childList: true,
          subtree: true,
        });

        reveal(group);

        return;
      }

      if (group.dataset.srGroup === "observe-only-children") {
        const observer = new MutationObserver(handleMutations.bind(null, group));

        observer.observe(group, {
          childList: true,
          subtree: true,
        });

        return;
      }

      if (group.dataset.srGroup !== "trigger") {
        reveal(group);
      }
    });
  },
  reveal,
};
